import { Utensils, Clock, Phone } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { useLanguage } from "@/i18n/LanguageContext";
import RoomCarousel from "@/components/site/RoomCarousel";

const A = "https://customer-assets.emergentagent.com/job_novi-hotel/artifacts";

const FOOD_IMAGES = [
  { src: `${A}/xkqykfj1_55.jpg`, alt: "Sala con tovaglie verdi e quadri" },
  { src: `${A}/p8vqfj9k_caption.jpg`, alt: "Sala con quadro del cavallo" },
  { src: `${A}/oiz35bxq_777.jpg`, alt: "Vista completa della sala" },
  { src: `${A}/8s45s1nd_66.jpg`, alt: "Saletta privata con tavolo a ferro di cavallo" },
  { src: `${A}/4r8g0tib_ee.jpg`, alt: "Lavagna con menu del giorno" },
  { src: `${A}/y4m8zmf5_eee.jpg`, alt: "Selezione di vini del Monferrato" },
  { src: `${A}/e3jtz6p1_33.jpg`, alt: "Cestino di pane fresco e focaccia" },
  { src: `${A}/kgvnoswp_b.jpg`, alt: "Bruschetta al pomodoro" },
  { src: `${A}/q6dvbtyp_s.jpg`, alt: "Salame affettato" },
  { src: `${A}/i6203tgd_p.jpg`, alt: "Prosciutto cotto e contorno" },
  { src: `${A}/xjjli7yw_lllllllllllllllllzzzzz.jpg`, alt: "Zuppa di pesce con cozze e crostini" },
  { src: `${A}/36jljhbs_tt.jpg`, alt: "Trippa con patate al sugo" },
  { src: `${A}/hzia96ve_44.jpg`, alt: "Vino della casa Vitidautunno" },
  { src: `${A}/e1izsspl_tttttt.jpg`, alt: "Torta della casa con gelato" },
];

export default function Restaurant() {
  const headRef = useReveal();
  const imgRef = useReveal();
  const { t } = useLanguage();

  return (
    <section
      id="ristorante"
      data-testid="restaurant-section"
      className="py-24 md:py-36 bg-[#F0EFEB]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div
          ref={headRef}
          className="reveal grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20 md:mb-28 items-end"
        >
          <div className="lg:col-span-7">
            <span className="overline block mb-5">{t.restaurant.overline}</span>
            <h2 className="font-serif-display text-4xl md:text-6xl font-light tracking-tight text-[#1A1C19] leading-[1.02]">
              {t.restaurant.titleLine1}
              {t.restaurant.titleLine2 && (
                <span className="italic block text-[#363A33]">
                  {t.restaurant.titleLine2}
                </span>
              )}
            </h2>
          </div>
          <p className="lg:col-span-5 text-base md:text-lg text-[#5C6058] leading-relaxed">
            {t.restaurant.intro}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Food carousel */}
          <div ref={imgRef} className="reveal lg:col-span-7 self-start">
            <RoomCarousel images={FOOD_IMAGES} name={t.restaurant.label} />
            <div className="mt-4 flex items-baseline justify-between text-xs uppercase tracking-[0.22em] text-[#5C6058]">
              <span>{t.restaurant.label}</span>
              <span className="italic font-serif-display text-sm normal-case tracking-normal text-[#A88B7D]">
                {t.restaurant.note}
              </span>
            </div>
          </div>

          {/* Right column: description + details */}
          <div className="lg:col-span-5 flex flex-col gap-10">
            <p className="text-base md:text-lg text-[#1A1C19] leading-relaxed font-light">
              {t.restaurant.description}
            </p>

            <div data-testid="restaurant-specialties" className="border-t border-[#E2E0D9] pt-8">
              <Utensils size={22} strokeWidth={1.25} className="text-[#363A33] mb-4" />
              <div className="overline mb-3">{t.restaurant.specialtiesLabel}</div>
              <ul className="space-y-2 text-base md:text-lg text-[#5C6058] font-light">
                {t.restaurant.specialties.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>

            <div data-testid="restaurant-hours" className="border-t border-[#E2E0D9] pt-8">
              <Clock size={22} strokeWidth={1.25} className="text-[#363A33] mb-4" />
              <div className="overline mb-3">{t.restaurant.hoursLabel}</div>
              <ul className="space-y-2 text-base md:text-lg text-[#5C6058] font-light">
                {t.restaurant.hours.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
            </div>

            <div data-testid="restaurant-reservation" className="border-t border-[#E2E0D9] pt-8">
              <Phone size={22} strokeWidth={1.25} className="text-[#363A33] mb-4" />
              <div className="overline mb-3">{t.restaurant.reservationLabel}</div>
              <p className="text-base md:text-lg text-[#5C6058] font-light leading-relaxed">
                {t.restaurant.reservationText}{" "}
                <a
                  href="tel:+3901432935"
                  className="text-[#1A1C19] underline-offset-4 hover:underline hover:text-[#A88B7D] transition-colors"
                >
                  +39 0143 2935
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
